import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

const STATUS_COLORS = {
  open: { bg: '#E6F1FB', color: '#0C447C' },
  pending: { bg: '#FAEEDA', color: '#633806' },
  resolved: { bg: '#EAF3DE', color: '#27500A' },
  closed: { bg: '#f0f0f0', color: '#555' },
};
const PRIORITY_COLORS = {
  urgent: { bg: '#FCEBEB', color: '#791F1F' },
  high: { bg: '#FAEEDA', color: '#633806' },
  normal: { bg: '#f5f5f5', color: '#555' },
  low: { bg: '#f5f5f5', color: '#888' },
};

const Badge = ({ label, colors }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', background: colors.bg, color: colors.color }}>
    {label}
  </span>
);

export default function TicketList() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [staff, setStaff] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [renderError, setRenderError] = useState(null);
  const [scheduledTickets, setScheduledTickets] = useState(null);
  const navigate = useNavigate();

  const openTicketCount = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const status = searchParams.get('status') ?? 'open';
  const statusFilter = status === 'all' ? '' : status;
  const assigned = searchParams.get('assigned') || '';
  const category = searchParams.get('category') || '';

  // Debounce keystrokes so we don't fire a request per character
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // A new search or filter always starts from page 1 — a stale page's OFFSET
  // silently skips past the results
  useEffect(() => { setPage(1); }, [debouncedSearch, status, assigned, category]);

  useEffect(() => {
    const checkNewTickets = async () => {
      try {
        const { data } = await api.get('/tickets', { params: { status: 'open', limit: 1, page: 1 } });
        const currentCount = data.total;
        if (openTicketCount.current !== null && currentCount > openTicketCount.current) {
          const newCount = currentCount - openTicketCount.current;
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);
            oscillator.frequency.value = 520;
            gainNode.gain.value = 0.1;
            oscillator.start();
            setTimeout(() => oscillator.stop(), 200);
          } catch (e) {}
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            new Notification(`${newCount} new ticket${newCount > 1 ? 's' : ''}`, {
              body: 'New patient message received',
              icon: 'https://tapgp.co.uk/wp-content/uploads/2020/08/logofull2_edited.png'
            });
          }
        }
        openTicketCount.current = currentCount;
      } catch (e) {}
    };
    checkNewTickets();
    const interval = setInterval(checkNewTickets, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (status !== 'scheduled') { setScheduledTickets(null); return; }
    const fetchScheduled = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/tickets/scheduled');
        setScheduledTickets(data.tickets);
        setTotal(data.total);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchScheduled();
    const interval = setInterval(fetchScheduled, 60000);
    return () => clearInterval(interval);
  }, [status]);

  // Abort superseded requests so a slow response for an old search term can
  // never overwrite the results of a newer one
  const abortRef = useRef(null);

  const fetchTickets = useCallback(async () => {
    if (status === 'scheduled') return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (assigned) params.assigned_to = assigned;
      if (debouncedSearch) params.search = debouncedSearch;
      if (category) params.category_id = category;
      params.page = page;
      params.limit = 30;
      const [ticketsRes, catsRes, staffRes] = await Promise.all([
        api.get('/tickets', { params, signal: controller.signal }),
        api.get('/categories', { signal: controller.signal }),
        api.get('/staff', { signal: controller.signal }),
      ]);
      setTickets(ticketsRes.data.tickets);
      setTotal(ticketsRes.data.total);
      setCategories(catsRes.data.categories || []);
      setStaff(staffRes.data.staff || []);
      setSelected([]);
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
      console.error(err);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, [status, statusFilter, assigned, debouncedSearch, page, category]);

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 60000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === tickets.length) {
      setSelected([]);
    } else {
      setSelected(tickets.map(t => t.id));
    }
  };

  const bulkUpdate = async (newStatus) => {
    if (!selected.length) return;
    if (!window.confirm(`Set ${selected.length} ticket(s) to "${newStatus}"?`)) return;
    setBulkLoading(true);
    try {
      await Promise.all(selected.map(id => api.patch(`/tickets/${id}`, { status: newStatus })));
      fetchTickets();
    } catch (err) {
      alert('Some updates failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const selectStyle = {
    padding: '8px 10px', border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '14px', background: '#fff',
  };

  if (renderError) {
    return (
      <div style={{ padding: '20px', background: '#FCEBEB', borderRadius: '8px', fontSize: '13px', color: '#791F1F' }}>
        <strong>Render error (please screenshot and report):</strong><br />
        {renderError}
      </div>
    );
  }

  let ticketRows;
  try {
    ticketRows = tickets.map((t, i) => (
      <div key={t.id}
        style={{
          padding: isMobile ? '12px 12px' : '12px 16px',
          borderBottom: i < tickets.length - 1 ? '1px solid #f0f0f0' : 'none',
          background: selected.includes(t.id) ? '#f0f7ff' : '',
          cursor: 'pointer',
        }}
        onMouseEnter={e => { if (!selected.includes(t.id)) e.currentTarget.style.background = '#fafafa'; }}
        onMouseLeave={e => { if (!selected.includes(t.id)) e.currentTarget.style.background = ''; }}
      >
        {isMobile ? (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <input type="checkbox" checked={selected.includes(t.id)}
              onChange={e => toggleSelect(t.id, e)}
              onClick={e => e.stopPropagation()}
              style={{ cursor: 'pointer', marginTop: '3px', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: '500', flex: 1, minWidth: 0 }}
                  onClick={() => navigate(`/tickets/${t.id}`)}>
                  {t.subject}
                </span>
                <span style={{ fontSize: '11px', color: '#999', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {t.updated_at ? formatDistanceToNow(new Date(t.updated_at), { addSuffix: true }) : ''}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}
                onClick={() => navigate(`/tickets/${t.id}`)}>
                {t.category_id && categories.find(c => c.id === t.category_id) && (
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', background: categories.find(c => c.id === t.category_id)?.color + '22', color: categories.find(c => c.id === t.category_id)?.color }}>
                    {categories.find(c => c.id === t.category_id)?.name}
                  </span>
                )}
                <Badge label={t.status} colors={STATUS_COLORS[t.status] || STATUS_COLORS.open} />
                {t.has_failed_message && (
                  <span title="A scheduled message failed to send" style={{ fontSize: '11px', background: '#FCEBEB', color: '#791F1F', padding: '1px 7px', borderRadius: '6px', fontWeight: '600', border: '1px solid #F09595' }}>
                    ⚠️ Send failed
                  </span>
                )}
                {t.priority !== 'normal' && <Badge label={t.priority} colors={PRIORITY_COLORS[t.priority]} />}
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}
                onClick={() => navigate(`/tickets/${t.id}`)}>
                {t.patient_name || t.patient_email} � Ref: {t.reference}
                {t.assigned_name && <span> � {t.assigned_name}</span>}
                {!t.assigned_to && <span style={{ color: '#c00' }}> � Unassigned</span>}
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}
                onClick={e => e.stopPropagation()}>
                <select value={t.assigned_to || ''} onChange={async e => {
                  await api.patch(`/tickets/${t.id}`, { assigned_to: e.target.value || null });
                  fetchTickets();
                }} style={{ padding: '4px 6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', flex: '1', minWidth: '0', maxWidth: '140px' }}>
                  <option value="">Unassigned</option>
                  {staff.filter(s => s.is_active).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {t.status !== 'resolved' && (
                  <button onClick={async e => {
                    e.stopPropagation();
                    await api.patch(`/tickets/${t.id}`, { status: 'resolved' });
                    fetchTickets();
                  }} style={{ padding: '4px 10px', fontSize: '12px', background: '#EAF3DE', color: '#27500A', border: '1px solid #97C459', borderRadius: '4px', cursor: 'pointer' }}>
                    Resolve
                  </button>
                )}
                {t.status !== 'closed' && (
                  <button onClick={async e => {
                    e.stopPropagation();
                    await api.patch(`/tickets/${t.id}`, { status: 'closed' });
                    fetchTickets();
                  }} style={{ padding: '4px 10px', fontSize: '12px', background: '#f0f0f0', color: '#555', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '12px', alignItems: 'center' }}>
            <input type="checkbox" checked={selected.includes(t.id)}
              onChange={e => toggleSelect(t.id, e)}
              onClick={e => e.stopPropagation()}
              style={{ cursor: 'pointer' }} />
            <div onClick={() => navigate(`/tickets/${t.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: '500' }}>{t.subject}</span>
                {t.category_id && categories.find(c => c.id === t.category_id) && (
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', background: categories.find(c => c.id === t.category_id)?.color + '22', color: categories.find(c => c.id === t.category_id)?.color }}>
                    {categories.find(c => c.id === t.category_id)?.name}
                  </span>
                )}
                <Badge label={t.status} colors={STATUS_COLORS[t.status] || STATUS_COLORS.open} />
                {t.has_failed_message && (
                  <span title="A scheduled message failed to send" style={{ fontSize: '11px', background: '#FCEBEB', color: '#791F1F', padding: '1px 7px', borderRadius: '6px', fontWeight: '600', border: '1px solid #F09595' }}>
                    ⚠️ Send failed
                  </span>
                )}
                {t.priority !== 'normal' && <Badge label={t.priority} colors={PRIORITY_COLORS[t.priority]} />}
              </div>
              <div style={{ fontSize: '12px', color: '#888', display: 'flex', gap: '12px' }}>
                <span>{t.patient_name || t.patient_email}</span>
                <span>Ref: {t.reference}</span>
                {t.assigned_name && <span>to {t.assigned_name}</span>}
                {!t.assigned_to && <span style={{ color: '#c00' }}>Unassigned</span>}
                {t.next_scheduled_at && <span style={{ color: '#6B21A8', fontWeight: '500' }}>🕑 Sends {new Date(t.next_scheduled_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
              <div style={{ fontSize: '12px', color: '#999' }}>
                <div>{t.updated_at ? formatDistanceToNow(new Date(t.updated_at), { addSuffix: true }) : ''}</div>
                <div style={{ fontSize: '11px', color: '#bbb' }}>{t.updated_at ? new Date(t.updated_at).toLocaleString('en-GB') : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: '4px', flexDirection: 'column', alignItems: 'flex-end' }} onClick={e => e.stopPropagation()}>
                <select value={t.assigned_to || ''} onChange={async e => {
                  await api.patch(`/tickets/${t.id}`, { assigned_to: e.target.value || null });
                  fetchTickets();
                }} style={{ padding: '3px 6px', fontSize: '11px', border: '1px solid #ddd', borderRadius: '4px', maxWidth: '120px' }}>
                  <option value="">Unassigned</option>
                  {staff.filter(s => s.is_active).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {t.status !== 'resolved' && (
                    <button onClick={async e => {
                      e.stopPropagation();
                      await api.patch(`/tickets/${t.id}`, { status: 'resolved' });
                      fetchTickets();
                    }} style={{ padding: '3px 8px', fontSize: '11px', background: '#EAF3DE', color: '#27500A', border: '1px solid #97C459', borderRadius: '4px', cursor: 'pointer' }}>
                      Resolve
                    </button>
                  )}
                  {t.status !== 'closed' && (
                    <button onClick={async e => {
                      e.stopPropagation();
                      await api.patch(`/tickets/${t.id}`, { status: 'closed' });
                      fetchTickets();
                    }} style={{ padding: '3px 8px', fontSize: '11px', background: '#f0f0f0', color: '#555', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    ));
  } catch (e) {
    setTimeout(() => setRenderError(e.message), 0);
    ticketRows = null;
  }

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="search" placeholder="Search tickets, patients..." value={search}
          onChange={e => { setSearch(e.target.value); if (e.target.value) setSearchParams(p => { p.set('status', 'all'); return p; }); }}
          style={{ ...selectStyle, flex: isMobile ? '1 1 100%' : '0 0 240px', width: isMobile ? '100%' : '240px', boxSizing: 'border-box' }}
        />
        <button onClick={() => setSearchParams(p => { p.set('status', status === 'scheduled' ? 'open' : 'scheduled'); return p; })}
          style={{ padding: '8px 14px', border: '1px solid ' + (status === 'scheduled' ? '#6B21A8' : '#ddd'), borderRadius: '8px', fontSize: '14px', background: status === 'scheduled' ? '#F5F0FF' : '#fff', color: status === 'scheduled' ? '#6B21A8' : '#333', fontWeight: status === 'scheduled' ? '600' : '400', cursor: 'pointer', flexShrink: 0 }}>
          🕑 Scheduled
        </button>
        <select value={status === 'scheduled' ? 'all' : status} onChange={e => setSearchParams(p => { p.set('status', e.target.value); return p; })}
          style={{ ...selectStyle, flex: isMobile ? '1 1 calc(50% - 4px)' : '0 0 auto' }}>
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={assigned} onChange={e => setSearchParams(p => { p.set('assigned', e.target.value); return p; })}
          style={{ ...selectStyle, flex: isMobile ? '1 1 calc(50% - 4px)' : '0 0 auto' }}>
          <option value="">All agents</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <select value={searchParams.get('category') || ''} onChange={e => setSearchParams(p => { p.set('category', e.target.value); return p; })}
          style={{ ...selectStyle, flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
          <option value="">All categories</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobile ? '1 1 100%' : '0 0 auto', justifyContent: isMobile ? 'space-between' : 'flex-end', marginLeft: isMobile ? '0' : 'auto' }}>
          <span style={{ fontSize: '13px', color: '#666' }}>{total} ticket{total !== 1 ? 's' : ''}</span>
          {typeof Notification !== 'undefined' && Notification.permission !== 'granted' && (
            <button onClick={() => Notification.requestPermission()}
              style={{ padding: '8px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
              Enable notifications
            </button>
          )}
          <button onClick={() => navigate('/tickets/new')}
            style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
            + New ticket
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '10px 14px', background: '#E6F1FB', borderRadius: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#0C447C' }}>{selected.length} selected</span>
          <button onClick={() => bulkUpdate('resolved')} disabled={bulkLoading}
            style={{ padding: '5px 12px', background: '#27500A', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Mark resolved
          </button>
          <button onClick={() => bulkUpdate('closed')} disabled={bulkLoading}
            style={{ padding: '5px 12px', background: '#555', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Close
          </button>
          <button onClick={() => bulkUpdate('open')} disabled={bulkLoading}
            style={{ padding: '5px 12px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Reopen
          </button>
          <button onClick={() => setSelected([])}
            style={{ padding: '5px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}

      {/* Ticket list */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden' }}>
        {tickets.length > 0 && (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '10px', background: '#fafafa' }}>
            <input type="checkbox" checked={selected.length === tickets.length && tickets.length > 0}
              onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
            <span style={{ fontSize: '12px', color: '#999' }}>Select all</span>
          </div>
        )}

        {status === 'scheduled' && scheduledTickets !== null && scheduledTickets.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>No scheduled messages</div>
        ) : status === 'scheduled' && scheduledTickets ? (
          scheduledTickets.map((t, i) => (
            <div key={t.id} onClick={() => navigate('/tickets/' + t.id)}
              style={{ padding: '12px 16px', borderBottom: i < scheduledTickets.length - 1 ? '1px solid #f0f0f0' : 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>{t.subject}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{t.patient_name || t.patient_email} &middot; Ref: {t.reference}{t.assigned_name ? ' · ' + t.assigned_name : ''}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '12px', background: '#EDE9FE', color: '#6B21A8', padding: '2px 8px', borderRadius: '6px', fontWeight: '500', marginBottom: '4px' }}>
                  🕑 {t.next_scheduled_at ? new Date(t.next_scheduled_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Scheduled'}
                </div>
                {t.has_failed_message && <div style={{ fontSize: '11px', background: '#FCEBEB', color: '#791F1F', padding: '1px 6px', borderRadius: '4px', border: '1px solid #F09595' }}>⚠️ Send failed</div>}
              </div>
            </div>
          ))
        ) : loading && tickets.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>Loading tickets...</div>
        ) : status !== 'scheduled' && tickets.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>No tickets found</div>
        ) : status !== 'scheduled' ? ticketRows : null}

        {/* Pagination */}
        {total > 30 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, background: '#fff' }}>
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#666' }}>
              {isMobile ? `${page}/${Math.ceil(total / 30)}` : `Page ${page} of ${Math.ceil(total / 30)} (${total} tickets)`}
            </span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 30)}
              style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', cursor: page >= Math.ceil(total / 30) ? 'not-allowed' : 'pointer', opacity: page >= Math.ceil(total / 30) ? 0.5 : 1, background: '#fff' }}>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}